import type { EligibilityCheckResponse } from '../../services/api';

/**
 * How stale a completed check is allowed to be before we nudge for a re-check.
 *
 * Mirrors `has_recent_completed_eligibility_check`'s 30-day default on the
 * backend, which the auth queue already uses.
 */
export const ELIGIBILITY_STALE_AFTER_DAYS = 30;

export type EligibilityFreshness =
  'none' | 'pending' | 'fresh' | 'stale' | 'failed' | 'inactive';

/**
 * The status a check really has, which is not always the one on the column.
 *
 * A 271 can come back `completed` and still be a rejection: the payer's answer
 * carries `raw_response.error` or a non-empty `errors` array. Shared with the
 * intake step, which labels it "Completed with error".
 */
export type EffectiveEligibilityStatus =
  EligibilityCheckResponse['status'] | 'completed_with_error';

const ACTIVE_COVERAGE_CODES = new Set(['1', '2', '3', '4', '5']);
const INACTIVE_COVERAGE_CODES = new Set(['6', '7', '8']);
// Most specific first: Home Health Care (42), then plan coverage (30), then all.
const COVERAGE_SCOPES: (string | null)[] = ['42', '30', null];

export type CoverageActivity = 'active' | 'inactive' | 'unknown';

/**
 * What the 271 said about coverage. Mirrors
 * `backend/app/services/eligibility_coverage_status.py`: a single inactive row
 * is not a lapse (a Part A-only Medicare patient gets an inactive Part B row),
 * so the STC 42 rows decide, else STC 30, else all rows; any active row wins.
 * No active or inactive code at all is `unknown`.
 */
export function coverageActivity(
  check: EligibilityCheckResponse | null
): CoverageActivity {
  if (!check || check.status !== 'completed') return 'unknown';
  const benefits: unknown = check.raw_response?.benefitsInformation;
  if (!Array.isArray(benefits)) return 'unknown';
  const rows = benefits.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const { code, serviceTypeCodes } = item as {
      code?: unknown;
      serviceTypeCodes?: unknown;
    };
    if (typeof code !== 'string' || !code) return [];
    const stcs = Array.isArray(serviceTypeCodes)
      ? serviceTypeCodes.filter((c): c is string => typeof c === 'string')
      : [];
    return [{ code, stcs }];
  });
  for (const scope of COVERAGE_SCOPES) {
    const codes = rows
      .filter(row => scope === null || row.stcs.includes(scope))
      .map(row => row.code);
    if (codes.some(code => ACTIVE_COVERAGE_CODES.has(code))) return 'active';
    if (codes.some(code => INACTIVE_COVERAGE_CODES.has(code))) {
      return 'inactive';
    }
  }
  return 'unknown';
}

/** Whether the 271 said coverage is inactive (EB01 6–8). */
export function isCoverageInactive(
  check: EligibilityCheckResponse | null
): boolean {
  return coverageActivity(check) === 'inactive';
}

export function getEffectiveStatus(
  check: EligibilityCheckResponse
): EffectiveEligibilityStatus {
  if (
    check.status === 'completed' &&
    (check.raw_response?.error ||
      ((check.raw_response?.errors as Array<Record<string, unknown>>) || [])
        .length > 0)
  ) {
    return 'completed_with_error';
  }
  return check.status;
}

/**
 * Parse a check timestamp as the UTC instant it is.
 *
 * `request_date` / `response_date` are naive UTC columns, so they serialize
 * without an offset and `new Date(...)` would read them as local time.
 */
export function parseCheckInstant(value: string): Date {
  const hasZone = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value);
  return new Date(hasZone ? value : `${value}Z`);
}

/** The most recent check wins; checks without a response date have not landed. */
export function latestCheck(
  checks: EligibilityCheckResponse[]
): EligibilityCheckResponse | null {
  const dated = checks.filter(c => c.response_date ?? c.request_date);
  if (dated.length === 0) return null;
  return dated.reduce((newest, c) =>
    parseCheckInstant(c.response_date ?? c.request_date) >
    parseCheckInstant(newest.response_date ?? newest.request_date)
      ? c
      : newest
  );
}

/**
 * The newest completed check that carries a clean CMS periods-of-care answer.
 * Deliberately as simple as `latestCheck`, so a server-side badge can mirror it.
 */
export function latestCmsAnswer(
  checks: EligibilityCheckResponse[]
): EligibilityCheckResponse | null {
  return latestCheck(
    checks.filter(
      c => c.status === 'completed' && c.other_agency_periods_of_care != null
    )
  );
}

/** Whole and fractional days since a check instant. */
export function checkAgeDays(instant: string): number {
  const ageMs = Date.now() - parseCheckInstant(instant).getTime();
  return ageMs / (1000 * 60 * 60 * 24);
}

export function freshnessOf(
  check: EligibilityCheckResponse | null
): EligibilityFreshness {
  if (!check) return 'none';

  const status = getEffectiveStatus(check);

  // None of these verified anything, and reading them as a green "Verified" is
  // the one thing this line must never do.
  if (
    status === 'failed' ||
    status === 'completed_with_error' ||
    status === 'expired' ||
    status === 'cancelled'
  ) {
    return 'failed';
  }
  // A check that was sent but has not landed is not the same as never having
  // checked: showing "Not verified" invites a second, separately billed 270.
  if (status !== 'completed' || !check.response_date) return 'pending';

  const activity = coverageActivity(check);
  if (activity === 'inactive') return 'inactive';
  // A completed 271 that names no active coverage verified nothing. Same rule
  // as the Insurance Eligibility report, which lists it as Not verified.
  if (activity === 'unknown') return 'none';

  return checkAgeDays(check.response_date) > ELIGIBILITY_STALE_AFTER_DAYS
    ? 'stale'
    : 'fresh';
}
